'use client';
import { useEffect, useMemo, useState } from 'react';
import type { Country, Currency } from '../../constants';
import type { CartResponseResponse } from '../../model/cart/cart';
import type { CartDiscoveryPresentation } from '../../model/cart/cartDiscovery';
import { getCartDiscoverySnapshotKey, withCartRewardRemaining } from '../../utils/cartDiscovery';
import { useCartService } from './useCartService';

/** Who is shopping and what is in the bag: the inputs every reward quote is keyed on. */
export interface CartRewardShopper {
  cart: CartResponseResponse['cart'] | undefined;
  /** The cart response's flag; undefined until the cart has loaded. */
  isAuthenticated: boolean | undefined;
  currency: Currency;
  country?: Country;
  actorId?: string;
  customerId: string | number | null;
  isUserLoading: boolean;
}

function useCartRewardSnapshotKey({ cart, country, actorId, isAuthenticated, customerId }: CartRewardShopper) {
  return useMemo(
    () =>
      cart
        ? getCartDiscoverySnapshotKey({ cart, country, actorId, isAuthenticated: isAuthenticated ?? false, customerId })
        : '',
    [cart, country, actorId, isAuthenticated, customerId],
  );
}

/**
 * Reward products for the cart on screen. Quotes are only requested for signed-in shoppers with
 * items, and the previous quote for the same shopper stays visible while a refreshed one loads so
 * the discovery UI never flashes empty between cart edits.
 */
export function useCartRewardPresentation(shopper: CartRewardShopper) {
  const { cart, isAuthenticated, currency, country, actorId, customerId, isUserLoading } = shopper;
  const { useGetRewardProductsQuery } = useCartService();
  const snapshotKey = useCartRewardSnapshotKey(shopper);
  const identity = JSON.stringify([country, currency, actorId, isAuthenticated, customerId]);
  const enabled = !isUserLoading && isAuthenticated === true && (cart?.item.length ?? 0) > 0;
  const rewards = useGetRewardProductsQuery({ currency, actorId }, { enabled, cartSnapshotKey: snapshotKey });
  const [settledIdentity, setSettledIdentity] = useState<string | null>(null);
  const [previousRewards, setPreviousRewards] = useState<{
    identity: string;
    data: NonNullable<typeof rewards.data>;
  }>();
  useEffect(() => {
    setPreviousRewards((previous) => {
      if (!enabled) {
        return undefined;
      }
      if (
        rewards.data &&
        !rewards.isPlaceholderData &&
        !rewards.isError &&
        rewards.data.cartSnapshotKey === snapshotKey
      ) {
        return { identity, data: rewards.data };
      }
      return previous?.identity === identity ? previous : undefined;
    });
  }, [enabled, identity, rewards.data, rewards.isPlaceholderData, rewards.isError, snapshotKey]);
  const rewardData =
    rewards.data ?? (rewards.isFetching && previousRewards?.identity === identity ? previousRewards.data : undefined);
  useEffect(() => {
    if (enabled && rewards.isFetched && !rewards.isPlaceholderData) {
      setSettledIdentity(identity);
    }
  }, [enabled, rewards.isFetched, rewards.isPlaceholderData, identity]);
  const progressBar = cart?.progressBar;
  const presentation = useMemo<CartDiscoveryPresentation | undefined>(() => {
    const isCurrent =
      enabled &&
      !rewards.isError &&
      rewardData?.currency === currency &&
      (rewardData.cartSnapshotKey === snapshotKey || rewards.isFetching);
    if (!isCurrent) {
      return undefined;
    }
    return {
      currency,
      cartSnapshotKey: rewardData.cartSnapshotKey,
      groups: withCartRewardRemaining(
        rewardData.groups,
        rewardData.cartSnapshotKey === snapshotKey ? progressBar : null,
      ),
    };
  }, [enabled, rewards.isError, rewards.isFetching, rewardData, currency, snapshotKey, progressBar]);
  return {
    identity,
    presentation,
    isRefreshing: rewards.isFetching,
    isInitialLoading: enabled && rewards.isLoading && settledIdentity !== identity,
  };
}

/** Keep reward products warm while the cart is closed, re-asking after every cart write. */
export function useCartRewardProductsPrefetch({
  isCartError,
  cartUpdatedAt,
  enabled = true,
  ...shopper
}: CartRewardShopper & {
  isCartError: boolean;
  /** The cart query's `dataUpdatedAt`; a fresh cart response re-runs the prefetch. */
  cartUpdatedAt: number;
  enabled?: boolean;
}) {
  const { cart, isAuthenticated, currency, actorId, isUserLoading } = shopper;
  const { usePrefetchRewardProducts } = useCartService();
  const prefetchRewardProducts = usePrefetchRewardProducts();
  const snapshotKey = useCartRewardSnapshotKey(shopper);
  const canPrefetch =
    enabled &&
    !isUserLoading &&
    !isCartError &&
    isAuthenticated === true &&
    cart?.currency.currency === currency &&
    cart.item.length > 0;
  useEffect(() => {
    if (canPrefetch && snapshotKey) {
      void prefetchRewardProducts({ currency, actorId }, snapshotKey);
    }
  }, [canPrefetch, snapshotKey, currency, actorId, cartUpdatedAt, prefetchRewardProducts]);
}
